import * as signalR from "@microsoft/signalr"
import { useEffect, useMemo, useRef } from "react"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { jwtDecode } from "jwt-decode"


const getNormalizedToken = (rawToken: string | null) => {
  if (!rawToken) {
    return ""
  }

  return rawToken.replace(/['"]/g, "")
}

const getUserIdFromToken = (token: string): string | null => {
  try {
    const payload = jwtDecode<Record<string, string>>(token)

    return (
      payload?.nameid ??
      payload?.sub ??
      payload?.id ??
      payload?.userid ??
      payload?.userId ??
      payload?.UserId ??
      payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] ??
      null
    )
  } catch {
    return null
  }
}

export function useRestaurantDeliveryHub(onRefresh: () => void) {
  const refreshRef = useRef(onRefresh)

  const userId = useMemo(() => {
    const token = getNormalizedToken(localStorage.getItem(API_ROUTES.TOKEN))

    return getUserIdFromToken(token)
  }, [])

  useEffect(() => {
    refreshRef.current = onRefresh
  }, [onRefresh])

  useEffect(() => {
    if (!userId) {
      return
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(API_ROUTES.HUB.DELIVERY, {
        accessTokenFactory: () => getNormalizedToken(localStorage.getItem(API_ROUTES.TOKEN)),
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build()

    const handleRefresh = () => refreshRef.current()

    connection.on("delivery:new", handleRefresh)
    connection.on("delivery:status", handleRefresh)

    connection.onreconnected(async () => {
      await connection.invoke("JoinRestaurantGroup", userId)
    })

    const connect = async () => {
      try {
        await connection.start()
        await connection.invoke("JoinRestaurantGroup", userId)
      } catch {
        return
      }
    }

    connect()

    return () => {
      connection.stop()
    }
  }, [userId])
}
